import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';

const LOGO_DEV_BRAND_STALE_TIME = 24 * 60 * 60 * 1_000;

export const logoDevSocialPlatformSchema = z.enum([
    'facebook',
    'github',
    'instagram',
    'linkedin',
    'pinterest',
    'reddit',
    'snapchat',
    'telegram',
    'tumblr',
    'twitter',
    'wechat',
    'whatsapp',
    'youtube',
]);

const logoDevBrandApiSchema = z
    .object({
        brandmark: z.string().url().optional(),
        colors: z
            .array(
                z.object({
                    b: z.number().int().min(0).max(255),
                    g: z.number().int().min(0).max(255),
                    hex: z.string(),
                    r: z.number().int().min(0).max(255),
                }),
            )
            .default([]),
        description: z.string().optional(),
        domain: z.string(),
        indexed_at: z.string().optional(),
        logo: z.string().url().optional(),
        logo_blurhash: z.string().optional(),
        name: z.string(),
        social_banners: z.array(z.string().url()).default([]),
        socials: z.partialRecord(logoDevSocialPlatformSchema, z.string().url()).default({}),
    })
    .transform(({ indexed_at, logo_blurhash, social_banners, ...brand }) => ({
        ...brand,
        indexedAt: indexed_at,
        logoBlurhash: logo_blurhash,
        socialBanners: social_banners,
    }))
    .nullable();

export type LogoDevBrand = z.infer<typeof logoDevBrandApiSchema>;
export type LogoDevSocialPlatform = z.infer<typeof logoDevSocialPlatformSchema>;

export const parseLogoDevBrandResponse = (response: unknown) => logoDevBrandApiSchema.parse(response);

export const normalizeLogoDevDomain = (domainOrWebsite?: string) => {
    const trimmedValue = domainOrWebsite?.trim();

    if (!trimmedValue) {
        return '';
    }

    try {
        const url = new URL(/^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`);

        const domain = url.hostname.toLocaleLowerCase().replace(/^www\./, '');

        return domain.includes('.') && domain.length <= 253 ? domain : '';
    } catch {
        return '';
    }
};

const fetchLogoDevBrand = async (domainOrWebsite: string, signal: AbortSignal) => {
    try {
        const domain = normalizeLogoDevDomain(domainOrWebsite);
        const response = await authorizedAxiosInstance.get('/api/vendors/logo-dev/brand', {
            params: { domain },
            signal,
        });

        return parseLogoDevBrandResponse(response.data);
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

export const logoDevQueryKeys = {
    brand: (domainOrWebsite?: string) =>
        ['vendors', 'logo-dev', 'brand', normalizeLogoDevDomain(domainOrWebsite)] as const,
};

export const logoDevQueryOptions = {
    brand: (domainOrWebsite: string) =>
        queryOptions({
            queryFn: ({ signal }) => fetchLogoDevBrand(domainOrWebsite, signal),
            queryKey: logoDevQueryKeys.brand(domainOrWebsite),
            // Brand enrichment is decorative. Never amplify entitlement/provider failures.
            retry: false,
            staleTime: LOGO_DEV_BRAND_STALE_TIME,
        }),
};
